/**
 * Phase 2 MM11 — Artwork / Print Handoff tab (project studio).
 * Package list/create → detail (completeness, missing items) → panels /
 * copy review / dieline upload / checklist / exports. Every view carries
 * the honesty line: production-assist, NOT automatically print-ready.
 */

import { useCallback, useEffect, useState } from "react";
import {
  artworkExportDownloadUrl, createArtworkExport, createArtworkPackage,
  getArtworkChecklist, getArtworkCopyBlocks, getArtworkDielines,
  getArtworkExports, getArtworkPackages, getArtworkPanels,
  getVisualConceptSets, patchArtworkChecklist, reviewArtworkCopy,
  uploadArtworkDieline,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;
const OBJECTS = ["burger_box", "pizza_box", "fries_box", "cup", "sleeve",
  "delivery_bag", "sticker", "wrap_paper", "pouch", "jar_label",
  "bottle_label", "rigid_box", "folding_carton", "mailer_box", "other"];
const PKG_TYPES = ["designer_handoff", "supplier_handoff",
  "sales_concept_handoff", "print_prep_assist", "internal_review_pack"];
const CHK_STATUSES = ["not_started", "missing", "needs_review", "complete",
  "not_applicable"];

export function ArtworkTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [items, setItems] = useState<Row[]>([]);
  const [conceptSets, setConceptSets] = useState<Row[]>([]);
  const [active, setActive] = useState<Row | null>(null);
  const [section, setSection] = useState("panels");
  const [detail, setDetail] = useState<Row[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", package_type: "designer_handoff",
    packaging_object_type: "burger_box", concept_set_id: "",
    material_note: "" });

  const refresh = useCallback(async () => {
    const [r, cs] = await Promise.all([
      getArtworkPackages(projectId), getVisualConceptSets(projectId)]);
    if (r.outcome === "ok") setItems(r.data?.items ?? []);
    else setNotice(r.status === 403 ? t.permissionDenied : t.loadError);
    if (cs.outcome === "ok") setConceptSets(cs.data?.items ?? []);
  }, [projectId, t.permissionDenied, t.loadError]);

  const loadSection = useCallback(async (pkg: Row, sec: string) => {
    setActive(pkg); setSection(sec);
    const pkgId = String(pkg.id);
    const call = sec === "panels" ? getArtworkPanels
      : sec === "copy" ? getArtworkCopyBlocks
      : sec === "dieline" ? getArtworkDielines
      : sec === "checklist" ? getArtworkChecklist : getArtworkExports;
    const r = await call(projectId, pkgId);
    setDetail(r.outcome === "ok" ? r.data?.items ?? [] : []);
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onCreate(): Promise<void> {
    if (form.title.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createArtworkPackage(projectId, {
      ...form, concept_set_id: form.concept_set_id || null });
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setForm({ ...form, title: "" });
    await refresh();
  }

  async function onUpload(file: File): Promise<void> {
    if (!active) return;
    const r = await uploadArtworkDieline(projectId, String(active.id), file);
    setNotice(r.outcome === "ok" ? null : r.errorMessage);
    await loadSection(active, "dieline");
  }

  async function onExport(type: string): Promise<void> {
    if (!active) return;
    const r = await createArtworkExport(projectId, String(active.id), type);
    setNotice(r.outcome === "ok" ? null : r.errorMessage);
    await loadSection(active, "exports");
  }

  return (
    <section data-testid="project-artwork">
      <p className="po-disclaimer" data-testid="artwork-honesty">
        {t.notPrintReady}
      </p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}

      <div className="po-row" data-testid="artwork-create">
        <input placeholder={t.nameLabel} value={form.title}
               data-testid="artwork-title"
               onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select value={form.package_type} aria-label={t.packageType}
                onChange={(e) => setForm({ ...form, package_type: e.target.value })}>
          {PKG_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={form.packaging_object_type} aria-label={t.objectType}
                onChange={(e) => setForm({ ...form, packaging_object_type: e.target.value })}>
          {OBJECTS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={form.concept_set_id} aria-label={t.visualsTab}
                onChange={(e) => setForm({ ...form, concept_set_id: e.target.value })}>
          <option value="">—</option>
          {conceptSets.map((c) => (
            <option key={String(c.id)} value={String(c.id)}>{String(c.title)}</option>))}
        </select>
        <input placeholder={t.notesLabel} value={form.material_note}
               onChange={(e) => setForm({ ...form, material_note: e.target.value })} />
        <Button variant="primary" onClick={() => void onCreate()}>
          {t.newArtworkPackage}
        </Button>
      </div>

      {items.length === 0 ? <p data-testid="artwork-empty">{t.emptyList}</p> : (
        <table className="po-table" data-testid="artwork-list">
          <thead><tr><th>{t.nameLabel}</th><th>{t.packageType}</th>
            <th>{t.objectType}</th><th>{t.statusLabel}</th>
            <th>{t.completenessScore}</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={String(p.id)} style={{ cursor: "pointer" }}
                  onClick={() => void loadSection(p, "panels")}>
                <td>{String(p.title)}</td>
                <td>{String(p.package_type)}</td>
                <td>{String(p.packaging_object_type)}</td>
                <td><StatusPill tone={String(p.status).startsWith("approved")
                  ? "ok" : "info"} label={String(p.status)} /></td>
                <td>{String(p.completeness)}%</td>
              </tr>))}
          </tbody>
        </table>)}

      {active ? (
        <div data-testid="artwork-detail">
          <h2>{String(active.title)} — {String(active.completeness)}%</h2>
          {Array.isArray(active.missing_items) && active.missing_items.length ? (
            <p data-testid="artwork-missing">{t.missingItems}:{" "}
              {(active.missing_items as string[]).slice(0, 5).join(" · ")}</p>) : null}
          <nav className="po-tabs">
            {[["panels", t.panelsSection], ["copy", t.copySection],
              ["dieline", t.dielineSection], ["checklist", t.checklistSection],
              ["exports", t.exportsSection]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={section === k}
                      className={section === k ? "po-tab po-tab--active" : "po-tab"}
                      onClick={() => void loadSection(active, k)}>
                {label}
              </button>))}
          </nav>

          {section === "panels" ? (
            <ul data-testid="artwork-panels">
              {detail.map((p) => (
                <li key={String(p.id)}>
                  <strong>{String(p.panel_name)}</strong> (P{String(p.priority)},{" "}
                  {String(p.language)}) — {String(p.purpose)}
                  {p.required_copy ? <> · {String(p.required_copy)}</> : null}
                </li>))}
            </ul>) : null}

          {section === "copy" ? (
            <ul data-testid="artwork-copy">
              {detail.map((c) => (
                <li key={String(c.id)}>
                  <code>{String(c.copy_type)}</code>{" "}
                  <StatusPill tone={c.review_status === "approved" ? "ok"
                    : c.risk_level === "high" ? "warn" : "info"}
                              label={`${String(c.review_status)} · risk ${String(c.risk_level)}`} />{" "}
                  {String(c.text || "(empty)")}{" "}
                  <Button variant="secondary" onClick={() =>
                    void reviewArtworkCopy(projectId, String(active.id),
                      String(c.id), "approved").then(() => loadSection(active, "copy"))}>
                    ✓
                  </Button>
                </li>))}
            </ul>) : null}

          {section === "dieline" ? (
            <div data-testid="artwork-dieline">
              <label className="po-field">
                <span className="po-field__label">{t.uploadDieline}</span>
                <input type="file" accept=".pdf,.svg,.png,.jpg,.jpeg,.ai,.eps"
                       onChange={(e) => e.target.files?.[0] &&
                         void onUpload(e.target.files[0])} />
              </label>
              <ul>
                {detail.map((d) => (
                  <li key={String(d.id)}>
                    {String(d.filename)} — <StatusPill tone={
                      d.status === "accepted_for_planning" ? "ok" : "info"}
                      label={String(d.status)} />
                    {Array.isArray(d.warnings) && d.warnings.length
                      ? <> · {(d.warnings as string[])[0]}</> : null}
                  </li>))}
              </ul>
            </div>) : null}

          {section === "checklist" ? (
            <ul data-testid="artwork-checklist">
              {detail.map((c) => (
                <li key={String(c.id)}>
                  [{String(c.category)}] {String(c.item)}{" "}
                  <select value={String(c.status)}
                          onChange={(e) => void patchArtworkChecklist(
                            projectId, String(active.id), String(c.id),
                            e.target.value).then(() => loadSection(active, "checklist"))}>
                    {CHK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </li>))}
            </ul>) : null}

          {section === "exports" ? (
            <div data-testid="artwork-exports">
              <p>{t.notPrintReady}</p>
              {["markdown", "json", "zip"].map((x) => (
                <Button key={x} variant="secondary"
                        onClick={() => void onExport(x)}>{x}</Button>))}
              <ul>
                {detail.map((e) => (
                  <li key={String(e.id)}>
                    {String(e.export_type)} · {String(e.created_at).slice(0, 16)}
                    {e.export_type === "zip" ? (
                      <> · <a href={artworkExportDownloadUrl(projectId,
                        String(active.id), String(e.id))}>ZIP</a></>) : null}
                    {e.artifact_id ? (
                      <> · <a href={`/app/artifacts?focus=${e.artifact_id}`}>artifact</a></>) : null}
                  </li>))}
              </ul>
            </div>) : null}
        </div>) : null}
    </section>
  );
}
